/**
 * @file styliticsInsightsAnalytics.js
 * @description
 * This script integrates Stylitics Insights analytics into the Complete Look widget on the site.
 * It loads the Stylitics Insights script dynamically, initializes analytics tracking, and sets up event listeners
 * to track user interactions such as widget load, adding items to cart, item clicks, and item views within the widget.
 *
 * Main usage:
 * - Dynamically loads the Stylitics Insights analytics script based on provided settings.
 * - Initializes the StyliticsInsights instance when the Complete Look widget is shown in the minicart.
 * - Tracks and sends analytics events to Stylitics for user interactions within the Complete Look widget.
 * - Ensures analytics are only initialized if settings are present and the Stylitics script is available.
 */
'use strict';

import { queryFirst } from 'lilly/domUtil';
const styliticsSettingsContainer = queryFirst('#stylitics-analytics-settings');
const ITEM_IMAGE_TYPE = 'laydown';
const miniCartSheetContainer = queryFirst('#minicart');
const pdpContainer = queryFirst('.js-outer-pdp-container.pdp');
let isMiniToteInsightsInitialized = false;
let isPDPInsightsInitialized = false;
let styliticsInsightsPDP = null;
let styliticsInsightsMiniTote = null;
let globalStyliticsEventsInitialized = false;

let trackingQueue = Promise.resolve();
/**
 * Enqueues a tracking task to be executed in order
 * And to prevent blocking the main thread
 * @param {*} task - The tracking task to be executed.
 */
function enqueueTracking(task) {
    trackingQueue = trackingQueue
        .then(() => task())
        .catch(err => window.console.error('Stylitics tracking error:', err));
}

/**
 * get stylitics analytics settings
 * @return {Object|boolean} stylitics analytics settings
 */
function getSettings() {
    if (!styliticsSettingsContainer) return false;

    const { styliticsAnalyticsSettings } = styliticsSettingsContainer.dataset;
    if (!styliticsAnalyticsSettings) return false;

    let settings;
    try {
        settings = JSON.parse(styliticsAnalyticsSettings);
    } catch (e) {
        return false;
    }

    return settings;
}

/**
 * get data for initialize widget
 * @param {Object} settings - widget settings
 * @param {string} styliticsRequestedId - requested product id for the widget
 * @return {Object} widget settings
 */
function getRequestData(settings, styliticsRequestedId) {
    if (!settings || !styliticsRequestedId) return null;
    const payload = {
        client: settings.clientID,
        env: settings.trackingEnv,
        itemId: styliticsRequestedId,
        placement: settings.placement
    };

    return { payload };
}

/**
 * main function to initialize StyliticsInsights instance and set up event listeners
 */
function initializeGlobalStyliticsAnalyticsEvent() {
    // send trackVisitPage data to Stylitics globally on PDP visit
    document.body.addEventListener('stylitics:pdp:visited', (e) => {
        if (!styliticsInsightsPDP) return;
        styliticsInsightsPDP.trackVisitPage();
    });

    // Send add item to cart event to Stylitics: on click to "Add to Cart" button
    // inside Complete Look widget: with placement 'widget'
    // main PDP add to cart event: with placement 'main'
    document.body.addEventListener('stylitics:completeLook:addItemToCart', (event) => {
        const data = event && event.detail;
        const styliticsInsights = data.target === 'pdp' ? styliticsInsightsPDP : styliticsInsightsMiniTote;
        if (!styliticsInsights || !data || !data.pid) return;

        const analyticsData = {
            itemId: data.pid,
            placement: data.placement || 'widget'
        };
        if (data.price) analyticsData.price = data.price.split('$').pop();

        enqueueTracking(async () => {
            await styliticsInsights.trackAddItemToCart(analyticsData);
        });
    });

    globalStyliticsEventsInitialized = true;
}
/**
 * main function to initialize StyliticsInsights instance and set up event listeners
 * @param {StyliticsInsights} styliticsInsightsType - StyliticsInsights instance type
 * @param {HTMLElement} styliticsCompleteLookContainer - container of the Stylitics Complete Look widget
 */
function initializeStyliticsAnalyticsEvent(styliticsInsightsType, styliticsCompleteLookContainer) {
    // Send widget load event to Stylitics
    styliticsCompleteLookContainer.addEventListener('stylitics:widgetLoaded', () => {
        const styliticsInsights = styliticsInsightsType === 'pdp' ? styliticsInsightsPDP : styliticsInsightsMiniTote;
        enqueueTracking(async () => {
            await styliticsInsights.trackWidgetLoad();
        });
    });

    // Send item click event to Stylitics:
    // on click to "View Full Details" button inside Complete Look widget
    styliticsCompleteLookContainer.addEventListener('stylitics:completeLook:trackItemClick', event => {
        const data = event && event.detail;
        const styliticsInsights = styliticsInsightsType === 'pdp' ? styliticsInsightsPDP : styliticsInsightsMiniTote;
        if (!styliticsInsights || !data || !data.pid) return;
        const placement = data.placement && ['widget', 'main'].includes(data.placement) ? data.placement : 'widget';

        const analyticsData = {
            item: {
                remote_id: data.pid,
                item_id: Number(data.itemId)
            },
            outfitId: data.outfitId ? Number(data.outfitId) : null,
            placement: placement,
            position: data.index || 0
        };
        // send trackItemClick data to Stylitics without blocking the main thread
        enqueueTracking(async () => {
            styliticsInsights.trackClickItem(analyticsData);
        });
    });

    // Send item view event to Stylitics: on tab change in Complete Look widget
    styliticsCompleteLookContainer.addEventListener('stylitics:completeLook:trackItemView', (event) => {
        const data = event && event.detail;
        const styliticsInsights = styliticsInsightsType === 'pdp' ? styliticsInsightsPDP : styliticsInsightsMiniTote;
        if (!styliticsInsights || !data || !data.pid) return;

        const analyticsData = {
            item: {
                remote_id: data.pid,
                item_id: Number(data.itemId)
            },
            outfitId: data.outfitId ? Number(data.outfitId) : null,
            itemImageType: ITEM_IMAGE_TYPE,
            position: data.index || 0
        };
        // send trackItemView data to Stylitics without blocking the main thread
        enqueueTracking(async () => {
            await styliticsInsights.trackViewItem(analyticsData);
        });
    });

    // Send Outfit view event to Stylitics
    styliticsCompleteLookContainer.addEventListener('stylitics:completeLook:trackViewOutfit', e => {
        const data = e && e.detail;
        const styliticsInsights = styliticsInsightsType === 'pdp' ? styliticsInsightsPDP : styliticsInsightsMiniTote;
        if (!styliticsInsights || !data || !data.outfitId) return;

        const analyticsData = {
            outfit: {
                id: Number(data.outfitId),
                primary_requested_id: data.pid,
                primary_remote_id: data.pid,
                username: styliticsInsights.client
            },
            uiComponent: data.uiComponent || 'widget',
            outfitPosition: Number(data.index || 0)
        };
        // send trackViewOutfit data to Stylitics without blocking the main thread
        enqueueTracking(async () => {
            await styliticsInsights.trackViewOutfit(analyticsData);
        });
    });
    // Send Outfit click event to Stylitics:
    // on click an outfit/bundle inside the Custom Widget (any product image inside the bundle)
    styliticsCompleteLookContainer.addEventListener('stylitics:completeLook:trackClickOutfit', (e) => {
        const styliticsInsights = styliticsInsightsType === 'pdp' ? styliticsInsightsPDP : styliticsInsightsMiniTote;
        if (!styliticsInsights || !e.detail || !e.detail.outfitId) return;
        const data = e.detail;
        const analyticsData = {
            outfit: {
                id: Number(data.outfitId),
                primary_requested_id: data.pid,
                primary_remote_id: data.pid,
                username: styliticsInsights.client
            },
            uiComponent: data.uiComponent || 'call-to-action',
            outfitPosition: Number(data.index || 0)
        };
        // send trackClickOutfit data to Stylitics without blocking the main thread
        enqueueTracking(async () => {
            await styliticsInsights.trackClickOutfit(analyticsData);
        });
    });

    // Send the trackClickJumpLink event to Stylitics: on click to jumplink inside PDP Swiper Slider
    styliticsCompleteLookContainer.addEventListener('stylitics:jumplink:click', (e) => {
        const styliticsInsights = styliticsInsightsType === 'pdp' ? styliticsInsightsPDP : styliticsInsightsMiniTote;
        // send trackClickJumpLink data to Stylitics without blocking the main thread
        enqueueTracking(async () => {
            await styliticsInsights.trackClickJumpLink();
        });
    });
}
/**
 * Create a StyliticsInsights instance
 * @param {*} settings - widget settings
 * @param {boolean} isStyliticsInsightsInitialized - flag to track if browsed item event was sent
 */
async function createStyliticsInsightsInstance(settings, isStyliticsInsightsInitialized) {
    if (!settings || !settings.script) return;
    let styliticsCompleteLookContainer;
    let currentStyliticsInsights;
    // Determine the container based on placement
    if (settings.placement === 'mini-cart') {
        styliticsCompleteLookContainer = miniCartSheetContainer;
        currentStyliticsInsights = styliticsInsightsMiniTote;
    } else if (settings.placement === 'pdp') {
        styliticsCompleteLookContainer = queryFirst('#stylitics-pdp-api-widget');
        currentStyliticsInsights = styliticsInsightsPDP;
    }

    if (!styliticsCompleteLookContainer) return;

    let { styliticsRequestedId } = styliticsCompleteLookContainer.dataset || {};
    if (!styliticsRequestedId) {
        const miniCartStyliticsContainer = queryFirst('#stylitics-complete-look', styliticsCompleteLookContainer);
        styliticsRequestedId = miniCartStyliticsContainer.dataset?.styliticsRequestedId;
    }
    // Get request data for initializing StyliticsInsights
    let requestData = getRequestData(settings, styliticsRequestedId);
    // Use the asynchronous create method to create a new instance of StyliticsInsights
    // to track events inside the Complete Look widget
    if (!requestData || !requestData.payload || !window.StyliticsInsights) return;
    // Avoid re-initializing the StyliticsInsights instance for the same item
    if (isStyliticsInsightsInitialized && currentStyliticsInsights?.itemId === styliticsRequestedId) {
        return;
    }

    const styliticsInsights = await window.StyliticsInsights.create(requestData.payload);
    if (styliticsInsights) {
        if (settings.placement === 'mini-cart') {
            isMiniToteInsightsInitialized = true;
            styliticsInsightsMiniTote = styliticsInsights;
        }
        if (settings.placement === 'pdp') {
            isPDPInsightsInitialized = true;
            styliticsInsightsPDP = styliticsInsights;
        }
        // Initialize global Stylitics analytics events only once
        if (!globalStyliticsEventsInitialized) {
            initializeGlobalStyliticsAnalyticsEvent();
        }
        // Initialize Stylitics analytics events for the Complete Look widget only once per instance
        if (!isStyliticsInsightsInitialized) {
            initializeStyliticsAnalyticsEvent(settings.placement, styliticsCompleteLookContainer);
        }
    }
}

// Load stylitics script and initialize the StyliticsInsights instance

window.addEventListener('load', () => {
    const settings = getSettings();
    if (!settings || !settings.script) return;
    // Check if tracking is enabled based on OneTrust settings
    // OnetrustActiveGroups is a global data layer object set by OneTrust
    const onetrustActiveGroupsValue = window.OnetrustActiveGroups || '';
    if (!onetrustActiveGroupsValue || !~onetrustActiveGroupsValue.indexOf('C0003')) {
        console.log('Stylitics Analytics: Tracking is disabled by OneTrust settings');
        return;
    }
    import(/* webpackIgnore: true */ settings.script)
        .then(StyliticsInsights => {
            window.StyliticsInsights = StyliticsInsights.default;
            console.log('Stylitics Analytics: Script is loaded');
            // Initialize the StyliticsInsights instance for the mini cart
            miniCartSheetContainer.addEventListener('stylitics:minicart:show', () => {
                settings.placement = 'mini-cart';
                createStyliticsInsightsInstance(settings, isMiniToteInsightsInitialized).then(() => {
                    // Track widget load event after the StyliticsInsights instance is created
                    miniCartSheetContainer.dispatchEvent(new CustomEvent('stylitics:widgetLoaded'));
                });
            });
            // Initialize the StyliticsInsights instance for the PDP on PDP is loaded
            if (pdpContainer) {
                settings.placement = 'pdp';
                createStyliticsInsightsInstance(settings, false).then(() => {
                    // Track PDP visit event after the StyliticsInsights instance is created
                    document.body.dispatchEvent(new CustomEvent('stylitics:pdp:visited'));
                });
            }
            document.body.addEventListener('stylitics:pdp:widget:loaded', () => {
                settings.placement = 'pdp';
                createStyliticsInsightsInstance(settings, isPDPInsightsInitialized).then(() => {
                    const styliticsCompleteLookContainer = queryFirst('#stylitics-pdp-api-widget');
                    // Track widget load event after the StyliticsInsights instance is created
                    styliticsCompleteLookContainer?.dispatchEvent(new CustomEvent('stylitics:widgetLoaded'));
                });
            });
            // Mark the Stylitics script is loaded to make sure that all event listeners are added and ready to use
            styliticsSettingsContainer.dataset.isStyliticsScriptLoaded = 'true';
            console.log('Stylitics Analytics: Event listeners for analytics are set up');
        }).catch(err => {
            console.error('Stylitics Analytics: Failed to load Stylitics Insights script:', err);
        });
});
